import { api } from '@/api';
import { LanguageCultureInfoDto, MaintenanceModeInfo, SystemInfoDto } from '@/api/generator';
import { stopErrorPropagation } from '@/errorHandling';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

const MAINTENANCE_MODE_TIMEOUT_MS = 10_000;

export const useSystemStore = defineStore('system', () => {
  const frontendVersion: string = APP_VERSION;
  const frontendBuildDate: string = APP_BUILD_DATE;

  const version = ref<SystemInfoDto['version']>();
  const productVersion = ref<SystemInfoDto['productVersion']>();
  const siteName = ref<SystemInfoDto['siteName']>();
  const releaseDate = ref<SystemInfoDto['releaseDate']>();
  const installationDate = ref<SystemInfoDto['installationDate']>();
  const taskTypes = ref<SystemInfoDto['taskTypes']>();
  const useLiveTranslations = ref<SystemInfoDto['useLiveTranslations']>();
  const environmentName = ref<SystemInfoDto['environmentName']>();
  const isPrototype = ref<SystemInfoDto['isPrototype']>();
  const instructions = ref<SystemInfoDto['instructions']>();
  const manufacturer = ref<SystemInfoDto['manufacturer']>();
  const appInsightConnectionString = ref<SystemInfoDto['appInsightConnectionString']>();

  const languages = ref<LanguageCultureInfoDto[]>();

  const maintenanceModeInfo = ref<MaintenanceModeInfo | null>();

  async function fetchSystemData() {
    const { result } = await api.systemApi.getSystemInfo();
    if (result) {
      version.value = result.version;
      productVersion.value = result.productVersion;
      siteName.value = result.siteName;
      releaseDate.value = result.releaseDate;
      installationDate.value = result.installationDate;
      taskTypes.value = result.taskTypes;
      useLiveTranslations.value = result.useLiveTranslations;
      environmentName.value = result.environmentName;
      isPrototype.value = result.isPrototype;
      instructions.value = result.instructions;
      manufacturer.value = result.manufacturer;
      appInsightConnectionString.value = result.appInsightConnectionString;

      console.log(environmentName.value);
    }
  }

  const isProductionEnvironment = computed(() => environmentName.value === 'Production');

  async function fetchMaintenanceModeInfo() {
    const { success, result } = await api.systemApi.getMaintenanceModeInfo([stopErrorPropagation], {
      signal: AbortSignal.timeout(MAINTENANCE_MODE_TIMEOUT_MS),
    });

    // A failed request (including a timeout) keeps the previous value rather than wiping a
    // known maintenance state.
    if (success) {
      maintenanceModeInfo.value = result;
    }
  }

  async function fetchLanguages() {
    const { result } = await api.localizationApi.getLanguages();
    if (result) {
      languages.value = result;
    }
  }

  async function fetchAllData() {
    return Promise.all([fetchMaintenanceModeInfo(), fetchLanguages(), fetchSystemData()]);
  }

  return {
    version,
    productVersion,
    frontendVersion,
    frontendBuildDate,
    siteName,
    releaseDate,
    installationDate,
    taskTypes,
    maintenanceModeInfo,
    fetchAllData,
    fetchMaintenanceModeInfo,
    fetchSystemData,
    isProductionEnvironment,
    languages,
    useLiveTranslations,
    isPrototype,
    manufacturer,
    instructions,
    appInsightConnectionString,
    environmentName,
  };
});
